// Words for the Augment page's "Background pictures" section (screens/augment/AugmentScreen.tsx), kept apart so
// they can be tested. The run itself is backend/app/ml/background_images.py: copies of the project's reviewed
// pictures with the annotated objects erased (and crops of the free part of a picture), saved as Empty, so they
// train as background pictures. A fallback for projects whose users cannot photograph the empty station.
import type { BackgroundEstimate } from "../api/augment";
import { BACKGROUND_MIN, BACKGROUND_PER_IMAGES } from "./backgroundAdvice";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const mb = (bytes: number) => (bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.max(0.1, bytes / 1e6).toFixed(1)} MB`);

/** The rule and where the project stands: "40 pictures with objects want about 5 background pictures (1 in 10, at least 5); you have 2." */
export function backgroundRule(e: Pick<BackgroundEstimate, "with_objects" | "wanted" | "have">): string {
  if (e.with_objects === 0) return "No pictures with reviewed objects yet.";
  const have = e.have === 0 ? "you have none" : `you have ${e.have}`;
  return `${plural(e.with_objects, "picture")} with objects want about ${plural(e.wanted, "background picture")} (1 in ${BACKGROUND_PER_IMAGES}, at least ${BACKGROUND_MIN}); ${have}.`;
}

/** "3 background pictures from 2 of your 3 reviewed pictures (1 a crop of the free part)", then cost and storage, each a line. */
export function backgroundEstimateLines(e: BackgroundEstimate): string[] {
  if (e.candidates === 0) return ["No reviewed pictures with objects here yet: review some annotations first, then come back."];
  if (e.total === 0) {
    return [e.have >= e.wanted
      ? `Enough background pictures already (${e.have}). Type a number to make more anyway.`
      : "Type how many background pictures to make."];
  }
  const crops = e.crops ? ` (${e.crops === 1 ? "1 a crop" : `${e.crops} crops`} of the free part of a picture)` : "";
  const lines = [`${plural(e.total, "background picture")} from ${e.source_images} of your ${plural(e.candidates, "reviewed picture")}${crops}`];
  if (e.metered && e.credits !== undefined) {
    lines.push(`Cost: ${e.total} × ${e.credits_per_image} credit${e.credits_per_image === 1 ? "" : "s"} = ${e.credits} credits (you have ${e.balance}). You pay only for the pictures actually made.`);
  }
  const storage = e.storage_allowance
    ? `Storage: about ${mb(e.bytes_estimate)} more, ${mb(e.storage_used + e.bytes_estimate)} of your ${mb(e.storage_allowance)}`
    : `Storage: about ${mb(e.bytes_estimate)} more`;
  lines.push(e.storage_fits ? storage : `${storage}: too much. Make fewer.`);
  if (e.too_many) lines.push(`One run makes at most ${e.max_total.toLocaleString("en")} pictures.`);
  return lines;
}

/** Whether the estimate allows starting. */
export const canStartBackground = (e: BackgroundEstimate | null) =>
  !!e && e.total > 0 && e.storage_fits && !e.too_many && (!e.metered || e.credits === undefined || (e.balance ?? 0) >= e.credits);
